import React from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Descriptions,
  Progress,
  Divider,
  Collapse,
  Badge,
  Alert,
  Button,
} from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { FixedIncomeRequest } from "../helpers/shared-interfaces";
import { tabPaneStyle } from "../helpers/constants";
import { capitalizeFirstLetter } from "../helpers/utilities";

const { Panel } = Collapse;

interface MaturityDetailsCardProps {
  fixedIncomeRequest: FixedIncomeRequest;
  onCalculateBreakdown?: () => void;
}

export const MaturityDetailsCard: React.FC<MaturityDetailsCardProps> = ({
  fixedIncomeRequest,
  onCalculateBreakdown,
}) => {
  if (
    !fixedIncomeRequest.maturityDate &&
    !fixedIncomeRequest.issuedDate &&
    !fixedIncomeRequest.redeemableAmount
  ) {
    return null;
  }

  const {
    maturityDate,
    issuedDate,
    maturesIn,
    redeemableAmount,
    daysInvested,
    matured,
    allocationBreakdown,
  } = fixedIncomeRequest;

  const calculateProgress = (): number => {
    if (!issuedDate || !maturityDate) return 0;

    const start = moment(issuedDate);
    const end = moment(maturityDate);
    const now = moment();

    const totalDuration = end.diff(start, "days");
    const elapsed = now.diff(start, "days");

    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
  };

  const progress = calculateProgress();
  // A redeemed treasury bill has been paid out and its balance moved to a new placement.
  const isRedeemed = fixedIncomeRequest.state === "redeemed";
  const hasMatured = matured || (maturesIn !== undefined && maturesIn <= 0);
  const isNearMaturity =
    maturesIn !== undefined && maturesIn <= 30 && maturesIn > 0;

  const getTimeRemainingDisplay = (): string => {
    if (isRedeemed) return "Redeemed";
    if (hasMatured) return "Matured";
    if (maturesIn === undefined || maturesIn === null) return "N/A";

    const days = Math.max(0, maturesIn);
    return `${days} day${days !== 1 ? "s" : ""}`;
  };

  const hasValidBreakdown =
    allocationBreakdown?.success && allocationBreakdown?.data;

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Investment Maturity & Returns Overview</span>
          {isRedeemed ? (
            <Tag color="blue">Redeemed</Tag>
          ) : allocationBreakdown?.data?.timing && (
            <Tag color="orange">
              {capitalizeFirstLetter(allocationBreakdown?.data?.timing ?? "")}{" "}
            </Tag>
          )}
        </div>
      }
    >
      {/* Progress Circle - Centered at top */}
      {issuedDate && maturityDate && (
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Progress
            type="circle"
            percent={progress}
            status={
              isRedeemed
                ? "normal"
                : hasMatured
                ? "success"
                : isNearMaturity
                ? "normal"
                : "active"
            }
            strokeColor={
              isRedeemed
                ? "#bfbfbf"
                : hasMatured
                ? "#52c41a"
                : isNearMaturity
                ? "#faad14"
                : {
                    "0%": "#108ee9",
                    "100%": "#87d068",
                  }
            }
            width={180}
            strokeWidth={8}
            format={(percent) => (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                  {percent?.toFixed(1)}%
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#999",
                    marginTop: "4px",
                  }}
                >
                  {isRedeemed ? "Redeemed" : hasMatured ? "Completed" : "Progress"}
                </div>
              </div>
            )}
          />

          {/* Start & End dates in a row */}
          <Row
            justify="center"
            gutter={48}
            style={{ marginTop: 16 }}
          >
            <Col>
              <div style={{ color: "#999", fontSize: "12px" }}>Started</div>
              <div style={{ fontWeight: 600, color: "#666", fontSize: "14px" }}>
                {moment(issuedDate).format("DD MMM YYYY")}
              </div>
            </Col>
            <Col>
              <div style={{ color: "#999", fontSize: "12px" }}>Matures</div>
              <div style={{ fontWeight: 600, color: "#666", fontSize: "14px" }}>
                {moment(maturityDate).format("DD MMM YYYY")}
              </div>
            </Col>
          </Row>
        </div>
      )}

      <Divider style={{ marginTop: 0 }} />

      {/* Investment Summary Statistics - in a row */}
      <Row gutter={[16, 16]} justify="center">
        {redeemableAmount !== undefined && !isRedeemed && (
          <Col xs={12} sm={6} style={{ textAlign: "center" }}>
            <Statistic
              title="Redeemable Amount"
              value={redeemableAmount}
              precision={2}
              prefix="GHS"
              valueStyle={{
                fontSize: "20px",
                fontWeight: "bold",
                color:
                  redeemableAmount >= 0
                    ? "#3f8600"
                    : "#cf1322",
              }}
            />
          </Col>
        )}

        <Col xs={12} sm={6} style={{ textAlign: "center" }}>
          <div>
            <div style={{ fontSize: "14px", marginBottom: 4, color: "rgba(0, 0, 0, 0.45)" }}>
              {isRedeemed || hasMatured ? "Status" : "Days Remaining"}
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              {getTimeRemainingDisplay()}
            </div>
          </div>
        </Col>

        <Col xs={12} sm={6} style={{ textAlign: "center" }}>
          <div>
            <div style={{ fontSize: "14px", marginBottom: 4, color: "rgba(0, 0, 0, 0.45)" }}>
              Days Invested
            </div>
            <div style={{ fontSize: "20px", fontWeight: "bold" }}>
              {daysInvested || 0}
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "normal",
                  marginLeft: "4px",
                }}
              >
                days
              </span>
            </div>
          </div>
        </Col>

        {hasValidBreakdown && allocationBreakdown?.data?.interest_rate && (
          <Col xs={12} sm={6} style={{ textAlign: "center" }}>
            <div>
              <div style={{ fontSize: "14px", marginBottom: 4, color: "rgba(0, 0, 0, 0.45)" }}>
                Interest Rate
              </div>
              <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                {allocationBreakdown.data.interest_rate ?? "N/A"}
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "normal",
                    marginLeft: "4px",
                  }}
                >
                  %
                </span>
              </div>
            </div>
          </Col>
        )}
      </Row>

      {/* Show error message if allocation breakdown failed */}
      {/* Nothing is left to redeem once redeemed, so the breakdown no longer applies. */}
      {!isRedeemed && allocationBreakdown && !allocationBreakdown.success && (
        <Alert
          message="Allocation Breakdown Unavailable"
          description={
            allocationBreakdown.error ||
            "Unable to calculate allocation breakdown at this time."
          }
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          action={
            <Button size="small" onClick={onCalculateBreakdown}>
              Calculate
            </Button>
          }
        />
      )}

      {/* Collapsible Breakdown Sections */}
      {hasValidBreakdown && !isRedeemed && (
        <Collapse style={{ marginTop: 16 }}>
          {/* Allocation Breakdown */}
          <Panel
            header={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span>
                  Allocation Breakdown <Badge count={1} style={tabPaneStyle} />
                </span>
                <Button
                  size="small"
                  style={{
                    borderColor: "#722ed1",
                    color: "#722ed1",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCalculateBreakdown?.();
                  }}
                >
                  Calculate New Breakdown
                </Button>
              </div>
            }
            key="allocation"
          >
            {/* Current Value at Maturity */}
            {allocationBreakdown?.data?.matured_redemption_data && (
              <div style={{ marginBottom: 24 }}>
                <Divider orientation="left">
                  <ClockCircleOutlined style={{ marginRight: 8 }} />
                  Current Value at Maturity
                </Divider>

                <Alert
                  message="Based on Current Redeemable Amount"
                  description={`Return if you hold the current redeemable amount of GHS ${parseFloat(
                    allocationBreakdown.data.redeemable_amount,
                  ).toFixed(2)} until maturity.`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item
                    label={
                      <span style={{ fontWeight: 500 }}>Interest Earned</span>
                    }
                  >
                    <span style={{ fontWeight: 600, fontSize: "16px" }}>
                      GHS{" "}
                      {parseFloat(
                        allocationBreakdown.data.matured_redemption_data
                          .total_matured_interest,
                      ).toFixed(2)}
                    </span>
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <span style={{ fontWeight: 500 }}>Total at Maturity</span>
                    }
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#722ed1",
                      }}
                    >
                      GHS{" "}
                      {parseFloat(
                        allocationBreakdown.data.matured_redemption_data
                          .total_matured_amount,
                      ).toFixed(2)}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}

            {/* Redemption Calculation */}
            {/* Treasury bill charges are shown when a redemption is created, not here. */}
            {allocationBreakdown?.data?.redemption_data &&
              !allocationBreakdown.data.treasury_bill && (
              <div>
                <Divider orientation="left">
                  <DollarOutlined style={{ marginRight: 8 }} />
                  Redemption Calculation
                </Divider>

                  <>
                    {allocationBreakdown.data.timing === "early" ? (
                      <Alert
                        message="Early Redemption"
                        description={`Redeeming on ${moment(
                          allocationBreakdown.data.redemption_data
                            .rediscount_date,
                        ).format(
                          "DD MMM YYYY",
                        )} (before maturity). 20% penalty applied to interest.`}
                        type="warning"
                        icon={<WarningOutlined />}
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    ) : (
                      <Alert
                        message="Matured Investment"
                        description={`Redeeming on ${moment(
                          allocationBreakdown.data.redemption_data
                            .rediscount_date,
                        ).format(
                          "DD MMM YYYY",
                        )} (at or after maturity). Full interest with no penalties.`}
                        type="success"
                        icon={<CheckCircleOutlined />}
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    <Descriptions
                      bordered
                      size="small"
                      column={{ xs: 1, sm: 2, md: 3 }}
                    >
                      <Descriptions.Item
                        label={
                          <span style={{ fontWeight: 500 }}>
                            Redemption Amount
                          </span>
                        }
                      >
                        <span style={{ fontWeight: 600, fontSize: "16px" }}>
                          GHS{" "}
                          {parseFloat(
                            allocationBreakdown.data.redemption_data
                              .redemption_amount,
                          ).toFixed(2)}
                        </span>
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <span style={{ fontWeight: 500 }}>
                            Interest Earned
                          </span>
                        }
                      >
                        <span style={{ fontWeight: 600, fontSize: "16px" }}>
                          GHS{" "}
                          {parseFloat(
                            allocationBreakdown.data.redemption_data
                              .redemption_interest_earned,
                          ).toFixed(2)}
                        </span>
                      </Descriptions.Item>

                      {allocationBreakdown.data.timing === "early" &&
                        parseFloat(
                          allocationBreakdown.data.redemption_data
                            .redemption_interest_penalty,
                        ) > 0 && (
                          <Descriptions.Item
                            label={
                              <span
                                style={{ fontWeight: 500, color: "#cf1322" }}
                              >
                                Penalty (20%)
                              </span>
                            }
                          >
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: "16px",
                                color: "#cf1322",
                              }}
                            >
                              GHS{" "}
                              {parseFloat(
                                allocationBreakdown.data.redemption_data
                                  .redemption_interest_penalty,
                              ).toFixed(2)}
                            </span>
                          </Descriptions.Item>
                        )}

                      <Descriptions.Item
                        label={
                          <span style={{ fontWeight: 500 }}>
                            Total Redemption Amount
                          </span>
                        }
                        span={
                          allocationBreakdown.data.timing === "early" ? 2 : 3
                        }
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: "18px",
                            color: "#722ed1",
                          }}
                        >
                          GHS{" "}
                          {parseFloat(
                            allocationBreakdown.data.redemption_data
                              .total_redemption_amount,
                          ).toFixed(2)}
                        </span>
                      </Descriptions.Item>

                      {allocationBreakdown.data.timing === "early" &&
                        parseFloat(
                          allocationBreakdown.data.redemption_data.loss,
                        ) > 0 && (
                          <Descriptions.Item
                            label={
                              <span style={{ fontWeight: 500 }}>
                                Loss vs Maturity
                              </span>
                            }
                          >
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: "16px",
                                color: "#fa8c16",
                              }}
                            >
                              GHS{" "}
                              {parseFloat(
                                allocationBreakdown.data.redemption_data.loss,
                              ).toFixed(2)}
                            </span>
                          </Descriptions.Item>
                        )}
                    </Descriptions>
                  </>
              </div>
            )}
          </Panel>
        </Collapse>
      )}
    </Card>
  );
};
